import { motion } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import CTA from "@/components/sections/CTA";
import SectorExplorer from "@/components/sections/SectorExplorer";
import { EDX } from "@/lib/api";
import { TIMELINE } from "@/lib/content";
import { Award, Download, Globe, IdCard, QrCode, Target, Trophy } from "lucide-react";

export default function About({ onOpenPalette }: { onOpenPalette: () => void }) {
  return (
    <>
      <Navbar onOpenPalette={onOpenPalette} />
      <main className="pt-28 pb-12 page-fade" data-testid="about-page">
        {/* Hero */}
        <section className="relative pt-16 pb-20">
          <div className="absolute inset-0 edx-aurora-bg opacity-30" />
          <div className="container-edx relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-edx-blue via-edx-purple to-edx-pink blur-3xl opacity-40" />
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10">
                  <img src={EDX.portrait} alt={EDX.founder} className="h-full w-full object-cover" data-testid="founder-portrait" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="section-index">EDX.01</div>
              <h1 className="display mt-4 text-4xl md:text-6xl text-white">
                Hey, I'm <span className="gradient-text">Eden</span>.
              </h1>
              <p className="mt-5 text-edx-muted leading-relaxed text-base">
                I'm a Junior Full-Stack Developer, Data Analyst and the Founder of EDENOX.
                I build with purpose, learn with intention, and genuinely love what I do.
              </p>
              <p className="mt-4 text-edx-muted leading-relaxed text-base">
                Born in <span className="text-white">Giyani, Limpopo</span>, raised in <span className="text-white">Ennerdale, Johannesburg</span>.
                I graduated <em>Cum Laude</em> twice at Nelson Mandela University and was the only woman in my 2023 IT Software Development class to do so.
              </p>
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { k: "2×", v: "Cum Laude" },
                  { k: "7", v: "Languages" },
                  { k: "8", v: "Certifications" },
                  { k: "16+", v: "Team" },
                ].map((s) => (
                  <div key={s.v} className="glass p-3 text-center">
                    <div className="display font-bold text-2xl gradient-text">{s.k}</div>
                    <div className="text-[10px] uppercase tracking-widest text-edx-muted font-mono">{s.v}</div>
                  </div>
                ))}
              </div>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <a href="/EDENOX-Company-Profile.pdf" download className="btn btn-primary" data-testid="about-company-profile">
                  <Download size={15} /> Company profile
                </a>
                <a href="/Eden-Thwala.vcf" download className="btn btn-ghost" data-testid="about-digital-card">
                  <IdCard size={15} /> Save digital contact
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="relative py-20">
          <div className="container-edx max-w-4xl">
            <div className="section-index">EDX.02</div>
            <h2 className="display mt-4 text-3xl md:text-5xl text-white">From a Windows XP <span className="gradient-text">to a tech company</span>.</h2>
            <div className="mt-8 space-y-5 text-edx-muted leading-relaxed text-base">
              <p>
                My love for technology started at home. I watched my older sister run a computer shop in Giyani — she bought parts and assembled PCs from scratch.
                When I passed Grade 3, she built me my very first computer. It ran Windows XP and I thought it was the most incredible thing I'd ever seen.
              </p>
              <p>
                That little girl with her Windows XP never went away. She just learned how to code.
              </p>
              <p>
                In 2020, COVID hit during my first year at Nelson Mandela University. The hands-on labs we were promised vanished — but a basic coding module
                introduced me to pseudocode and something <em>clicked</em>. I graduated Cum Laude and immediately registered for the Diploma in IT Software Development.
                In 2023 I graduated Cum Laude again — the only woman in my class to do so.
              </p>
              <p>
                After two years of professional full-stack development, I founded <span className="text-white font-semibold">EDENOX (PTY) LTD</span> on
                4 June 2025 to prove that world-class software can be engineered right here, in South Africa, by people who look like me — and to lift others as I climb.
              </p>
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="relative py-20">
          <div className="container-edx">
            <div className="section-index">EDX.03</div>
            <h2 className="display mt-4 text-3xl md:text-5xl text-white">The <span className="gradient-text">journey</span></h2>
            <div className="mt-12 relative">
              <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-edx-cyan via-edx-purple to-edx-pink" />
              <div className="space-y-8">
                {TIMELINE.map((t, i) => (
                  <motion.div key={t.year} data-testid={`timeline-${i}`}
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`relative pl-12 sm:pl-0 sm:grid sm:grid-cols-2 sm:gap-12 ${i % 2 ? "" : "sm:[direction:rtl]"}`}>
                    <div className={`${i % 2 ? "" : "sm:[direction:ltr]"} sm:text-right ${i % 2 ? "sm:text-left sm:col-start-2" : ""}`}>
                      <div className="glass p-5 inline-block max-w-md sm:max-w-none">
                        <div className="text-[11px] uppercase tracking-widest text-edx-cyan font-mono">{t.year}</div>
                        <div className="display font-bold text-white text-lg mt-1">{t.title}</div>
                        <p className="text-sm text-edx-muted mt-2">{t.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 sm:left-1/2 top-6 -translate-x-1/2 h-3 w-3 rounded-full bg-edx-pink shadow-[0_0_15px_#EC4899] z-10" />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mission/Vision */}
        <section className="relative py-20">
          <div className="container-edx grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Target, title: "Mission", color: "cyan", desc: "Empower businesses with cutting-edge software and IT solutions that drive efficiency, foster innovation, and accelerate growth." },
              { icon: Globe, title: "Vision", color: "purple", desc: "Become the trusted digital partner of choice across South Africa, Africa, and the global stage." },
              { icon: Award, title: "Values", color: "pink", desc: "Integrity · Excellence · Innovation · Youth empowerment & development." },
            ].map((b, i) => (
              <div key={i} className="glass glass-edge p-6">
                <div className={`h-11 w-11 rounded-xl bg-edx-${b.color}/15 text-edx-${b.color} flex items-center justify-center`}>
                  <b.icon size={20} />
                </div>
                <h3 className="display font-bold text-white text-xl mt-4">{b.title}</h3>
                <p className="text-sm text-edx-muted mt-2 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="relative py-20" data-testid="digital-business-card">
          <div className="container-edx">
            <div className="digital-card-panel">
              <div>
                <div className="section-index">EDX.04</div>
                <h2 className="display mt-4 text-3xl md:text-5xl text-white">Save Eden&apos;s contact <span className="gradient-text">in one scan</span>.</h2>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-edx-muted">
                  Scan the QR code from any modern iPhone or Android device, or download the vCard directly to add EDENOX founder and company contact details.
                </p>
                <a href="/Eden-Thwala.vcf" download className="btn btn-primary mt-7">
                  <IdCard size={15} /> Add to contacts
                </a>
              </div>
              <div className="digital-card-qr">
                <img src="/eden-thwala-contact-qr.svg" alt="QR code for Eden Thwala digital business card" />
                <span>IOS / ANDROID CONTACT</span>
              </div>
            </div>
          </div>
        </section>

        <SectorExplorer />

        <CTA />
      </main>
      <Footer />
    </>
  );
}
